import { useState } from "react";
import { ChevronDown, ChevronRight, Coins, Loader2 } from "lucide-react";
import api, { apiErr, fmtCreditBalance } from "../lib/api";
import { Card } from "./ui-bits";

const when = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const wholeCredits = value => {
  const amount = Math.abs(Number(value) || 0);
  return amount > 0 && amount < 0.5 ? "<1" : fmtCreditBalance(Math.round(amount));
};

export default function CreditSpending() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async (page = 1) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/credits/spending", { params: { page, limit: 20 } });
      setResult(previous => ({ ...data, items: page === 1 ? data.items : [
        ...(previous?.items || []), ...data.items.filter(item => !previous?.items?.some(old => old.id === item.id)),
      ] }));
    } catch (e) { setError(apiErr(e)); }
    finally { setBusy(false); }
  };

  const toggle = () => {
    if (!open && !result) load();
    setOpen(!open);
  };

  return <Card className="overflow-hidden" data-testid="credit-spending-history">
    <button onClick={toggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-slate-50">
      <span className="flex items-center gap-2 text-xl font-semibold text-slate-900">
        {open ? <ChevronDown size={19} /> : <ChevronRight size={19} />}
        <Coins size={22} className="text-blue-600" /> Historial de créditos
      </span>
    </button>
    {open && <div className="space-y-4 border-t border-slate-100 px-6 pb-6 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
        <span>Vídeos, shorts, recargas, altas, renovaciones, compensaciones y premios.</span>
        <button onClick={() => load()} disabled={busy} className="font-semibold text-blue-700 disabled:opacity-50">Actualizar</button>
      </div>
      {error && <p role="alert" className="text-sm text-rose-600">{error} <button className="font-semibold underline" onClick={() => load()}>Reintentar</button></p>}
      {result?.items?.length > 0 && <div className="divide-y divide-slate-100">
        {result.items.map(item => <div key={item.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-3">
          <div className="min-w-0">
            <div className="break-words font-medium text-slate-900">{item.label}</div>
            <div className="text-sm text-slate-500">{when(item.at)}</div>
          </div>
          <div className="ml-auto text-right tabular-nums">
            <div className={`font-semibold ${item.delta > 0 ? "text-emerald-700" : item.delta < 0 ? "text-rose-700" : "text-slate-700"}`}>
              {item.category === "opening" ? "Saldo de partida" : typeof item.delta === "number"
                ? `${item.delta > 0 ? "+" : item.delta < 0 ? "−" : ""}${wholeCredits(item.delta)} créditos`
                : "Créditos no registrados"}
            </div>
            {typeof item.balance_after === "number" && <div className="text-xs text-slate-500">
              Saldo después: {fmtCreditBalance(item.balance_after)} créditos
            </div>}
          </div>
        </div>)}
      </div>}
      {busy && <div role="status" className="flex items-center justify-center gap-2 py-3 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Cargando movimientos…
      </div>}
      {!busy && result?.items?.length === 0 && <p className="text-sm text-slate-500">Todavía no hay movimientos de créditos registrados.</p>}
      {result && result.items.length < result.total && <button disabled={busy} onClick={() => load(result.page + 1)}
        className="w-full rounded-lg border border-slate-200 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50">
        Ver más ({result.items.length} de {result.total})
      </button>}
    </div>}
  </Card>;
}
